<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';
import { useSidebarLayout } from 'next/sidebar/provider.js';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components/widgets/FloatingCallWidget.vue')
);

// O briefing do dia carrega só depois que o painel está de pé: ele é um
// extra na entrada e não pode atrasar a tela de ninguém.
const ModalDaOab = defineAsyncComponent(
  () => import('dashboard/components-next/escritorio/ModalDaOab.vue')
);
// Verificação em duas etapas exigida pelo escritório (Fase 2, parte 8).
const NovidadeDesfazerEnvio = defineAsyncComponent(
  () => import('dashboard/components-next/escritorio/NovidadeDesfazerEnvio.vue')
);
const NovidadeCaixaUnica = defineAsyncComponent(
  () => import('dashboard/components-next/escritorio/NovidadeCaixaUnica.vue')
);
const NovidadeCopiloto = defineAsyncComponent(
  () => import('dashboard/components-next/escritorio/NovidadeCopiloto.vue')
);
const AvisoDuasEtapas = defineAsyncComponent(
  () => import('dashboard/components-next/escritorio/AvisoDuasEtapas.vue')
);
const BriefingDoDia = defineAsyncComponent(
  () => import('dashboard/components-next/briefing/BriefingDoDia.vue')
);
// Alerta urgente (risco, WhatsApp caiu, incidente) em destaque — ver
// components-next/alertas/AlertaUrgente.vue. Os alertas saíam pelo WhatsApp
// do escritório até a Meta restringir o número (23/09/2026).
const AlertaUrgente = defineAsyncComponent(
  () => import('dashboard/components-next/alertas/AlertaUrgente.vue')
);

// O telefone do advup (softphone, 25/09/2026): gaveta e cartão da chamada.
// O botão fica na barra do topo; quem não tem ramal só faz uma consulta.
const Telefone = defineAsyncComponent(
  () => import('dashboard/components-next/telefone/Telefone.vue')
);

// A nova conversa do "Conversar" das telas jurídicas (02/10/2026): uma janela
// só para o painel inteiro, aberta por `useConversaDoContato`.
const NovaConversaDoContato = defineAsyncComponent(
  () =>
    import(
      'dashboard/components-next/NewConversation/NovaConversaDoContato.vue'
    )
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import MobileBottomNav from 'dashboard/components-next/sidebar/MobileBottomNav.vue';
import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
    MobileBottomNav,
    BriefingDoDia,
    ModalDaOab,
    AvisoDuasEtapas,
    NovidadeDesfazerEnvio,
    NovidadeCaixaUnica,
    NovidadeCopiloto,
    AlertaUrgente,
    Telefone,
    NovaConversaDoContato,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();
    const { isHorizontal } = useSidebarLayout();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      isHorizontal,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
    // advup PWA mobile (2026-07-27) — Fase 2.2: dentro de uma conversa
    // específica, a bottom nav some (padrão WhatsApp/Telegram — tab bar é
    // navegação, não convive com o chat aberto, que já tem seu próprio
    // "voltar" no header). Detecta pelo param real da rota
    // (`conversation_id`, ver conversation.routes.js) em vez de uma lista
    // de nomes de rota — não quebra se uma rota nova de conversa nascer
    // amanhã. Some só a barra; o resto do layout mobile continua igual.
    isViewingConversation() {
      // `conversationId` também: as rotas de menções, sem resposta,
      // participando e time usam o nome em camelCase (06/10/2026).
      const { conversation_id: id, conversationId } = this.$route.params;
      return Boolean(id || conversationId);
    },
    // TELA DE DETALHE NO CELULAR (06/10/2026): uma conversa aberta — do chat
    // ou do chat interno. Como no WhatsApp, a tela é da conversa: o
    // cabeçalho dela (com o "‹") vira a barra de navegação, e somem a barra
    // da marca em cima e as abas embaixo. Só abaixo de 640px; ver
    // `.advup-tela-detalhe` no fim deste arquivo.
    isTelaDeDetalhe() {
      return (
        this.isViewingConversation ||
        this.$route.name === 'internal_chat_detail'
      );
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div
    class="flex flex-grow overflow-hidden text-n-slate-12 advup-raiz"
    :class="[
      isHorizontal ? 'flex-col' : 'flex-row',
      { 'advup-tela-detalhe': isTelaDeDetalhe },
    ]"
  >
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <!-- advup PWA mobile — 4,75rem = a cápsula flutuante (3,75rem) + a margem de baixo e
         um respiro (24/10/2026; era 4rem + a folga do home indicator). Classe Tailwind (não style inline)
         de propósito — `sm:mb-0` só funciona como override de breakpoint
         real se os dois ficarem no mesmo sistema de classes; style inline
         tem especificidade maior e vazaria a margem pro desktop também.
         Fase 2.2 — dentro de uma conversa a barra some (embaixo), então a
         margem que compensava ela também não faz sentido mais. -->
    <main
      class="flex flex-1 min-h-0 px-0 overflow-hidden bg-n-surface-1 sm:mb-0"
      :class="[
        isHorizontal ? 'w-full' : 'h-full w-full',
        isTelaDeDetalhe ? '' : 'mb-[4.75rem]',
      ]"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          v-if="!isHorizontal"
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar />
        <CopilotLauncher />
        <MobileSidebarLauncher
          v-if="!isHorizontal"
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
        <MobileBottomNav
          v-if="!isTelaDeDetalhe"
          :is-more-open="isMobileSidebarOpen"
          @open-more="toggleMobileSidebar"
        />
        <BriefingDoDia />
        <!-- A OAB do escritório no primeiro acesso (Fase 2) -->
        <ModalDaOab />
        <AvisoDuasEtapas />
        <NovidadeDesfazerEnvio />
        <NovidadeCaixaUnica />
        <NovidadeCopiloto />
        <AlertaUrgente />
        <Telefone />
        <NovaConversaDoContato />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>

<style>
/* A faixa atrás da barra flutuante do celular (24/10/2026): é o fim da raiz, abaixo do
   <main>. Com o fundo do corpo (navy) ela virava uma linha escura sob a cápsula; com o chão das
   páginas (`.ficha-chao`, mesma receita), a cápsula flutua sobre o mesmo chão da tela. */
@media (width < 640px) {
  .advup-raiz {
    --chao-celular: color-mix(
      in srgb,
      rgb(var(--blue-9)) 4%,
      rgb(var(--slate-3))
    );
    background: var(--chao-celular);
  }

  /* O chão escuro é véu preto sobre o navy do <main>; aqui, a mesma cor já composta (sólida),
     para servir também ao esmaecer da barra. */
  .dark .advup-raiz {
    --chao-celular: color-mix(in srgb, rgb(var(--blue-9)) 7%, rgb(10 16 29));
  }
}

/* Tela de detalhe no celular — ver `isTelaDeDetalhe`. A faixa do relógio do
   iPhone (safe area) continua azul-marinho: com a barra da marca fora, ela
   ficaria clara, e o relógio do iOS no app instalado é BRANCO. */
@media (max-width: 639px) {
  .advup-tela-detalhe > header.advup-topbar-safe-area {
    display: none;
  }

  .advup-tela-detalhe > main {
    border-top: env(safe-area-inset-top, 0px) solid #0f172a;
  }
}
</style>
